import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import axios from "axios";
import "./CreateCoach.css";
import Swal from 'sweetalert2';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { buildDiffPreview, fireAudit } from "../utils/auditClient";

const coachTypeOptions = [
  { value: "HEAD", label: "Head" },
  { value: "JUNIOR", label: "Junior" },
];

const roleOptions = [
  { value: "CBU_AM", label: "CBU_AM" },
  { value: "CTU_member", label: "CTU_member" },
  { value: "NONE", label: "None" },
];

const genderOptions = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
];

const CreateCoach = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const queryCoachId = searchParams.get("coachId");
  const coachId = location.state?.coachId || queryCoachId;
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    mobileNumber: "",
    name: "",
    about: "",
    gender: null,
    experienceYear: "",
    experienceMonth: "",
    sportId: "",
    joiningDate: today,
    coachType: null,
    coachTypeAcademyId: null,
    role: null,
    academyIds: [],
  });
  const [data, setData] = useState({ sports: [], academies: [] });
  const baselineRef = useRef(null);

  const handleChange = (name, value) => {
    setFormData((prev) => {
      let updatedData = { ...prev, [name]: value };

      if (name === "role" && (value === "NONE" || value === "CTU_member")) {
        updatedData.academyIds = [];
      }

      return updatedData;
    });
  };

  const buildSubmitPayload = (fd = formData) => {
    let formattedRole = fd.role?.value || fd.role;
    if (formattedRole === "CBU_AM") formattedRole = "CBU";
    if (formattedRole === "CTU_member") formattedRole = "CTU";
    const formattedCoachType = fd.coachType?.value || fd.coachType;
    const formattedJoiningDate = fd.joiningDate
      ? new Date(fd.joiningDate).toISOString().split("T")[0]
      : null;

    return {
      coachId: coachId || null,
      mobileNumber: fd.mobileNumber || "",
      name: fd.name || "",
      about: fd.about || "",
      gender: fd.gender || "MALE",
      experienceYear: Number(fd.experienceYear) || 0,
      experienceMonth: Number(fd.experienceMonth) || 0,
      sportId: Number(fd.sportId) || null,
      joiningDate: formattedJoiningDate,
      coachType: formattedCoachType || "HEAD",
      coachTypeAcademyId: fd.coachTypeAcademyId ? Number(fd.coachTypeAcademyId) : null,
      role: formattedRole || "CBU",
      academyIds: Array.isArray(fd.academyIds) ? fd.academyIds.map((a) => Number(a)) : [],
    };
  };

  useEffect(() => {
    if (coachId) {
      const fetchCoachDetails = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_COACH_DETAILS}?coachId=${coachId}`,
            {
              headers: {
                'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.data.success) {
            const coachData = response.data.data.data;

            const matchedCoachType = coachTypeOptions.find(
              option => option.value.toLowerCase() === coachData.coachType?.toLowerCase()
            );
            const roleMapping = {
              cbu: "CBU_AM",
              ctu: "CTU_member",
            };

            const matchedRole = roleOptions.find(
              option => option.value === roleMapping[coachData.role] || option.value === coachData.role
            );

            setFormData((prev) => {
              const next = {
                ...prev,
                mobileNumber: coachData.mobileNumber?.replace(/^\+91\s*/, "") || "",
                name: coachData.name || "",
                about: coachData.about || "",
                gender: coachData.gender || null,
                experienceYear: coachData.experienceYear || "",
                experienceMonth: coachData.experienceMonth || "",
                sportId: coachData.sportId || "",
                joiningDate: coachData.joiningDate || "",
                coachType: matchedCoachType ? matchedCoachType : null,
                role: matchedRole || null,
                coachTypeAcademyId: coachData.coachTypeAcademyId || null,
                academyIds: coachData.academyIds || [],
              };
              baselineRef.current = buildSubmitPayload(next);
              return next;
            });
          }
        } catch (error) {

        }
      };

      fetchCoachDetails();
    } else {
      baselineRef.current = buildSubmitPayload(formData);
    }
  }, [coachId]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    const MAIN_ENDPOINT = process.env.REACT_APP_API_CREATE_COACH;

    const newPayload = buildSubmitPayload(formData);
    const oldPayload = baselineRef.current || {};

    if (coachId) {
      const { digest, html } = buildDiffPreview(oldPayload, newPayload);
      if (!digest.length) {
        Swal.fire({
          icon: "info",
          title: "Nothing to update",
          text: "No differences detected.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: "Apply these changes?",
        html,
        showCancelButton: true,
        confirmButtonText: "Update",
      });
      if (!confirm.isConfirmed) return;
    }

    try {
      const response = await axios.post(MAIN_ENDPOINT, newPayload, {
        headers: {
          "Content-Type": "application/json",
          "x-authorization": process.env.REACT_APP_AUTH_TOKEN,
        },
      });
      fireAudit({
        entity: "coach",
        affected_id: coachId || null,
        action: coachId ? "update" : "create",
        businessApi: MAIN_ENDPOINT,
        businessMethod: "POST",
        oldPayload,
        newPayload,
        section: 'CBU',
        subSection: 'Create Coach',
        extra: { page: "CreateCoach" },
      });

      if (response.data.success === true) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: coachId ? "Coach Updated Successfully!" : "Coach Added Successfully!",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
        baselineRef.current = newPayload;
        navigate("/coachList");
      } else {
        Swal.fire({
          icon: "warning",
          title: "Oops!",
          text: "Something went wrong. Please try again.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      const apiErrorMessage =
        error.response?.data?.error_message || "Failed to add/update coach.";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: apiErrorMessage,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }

  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_API_GET_ALL_ACADEMY, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          },
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const result = await response.json();
        setData({
          sports: result.data?.data?.sports?.map((sport) => ({
            id: sport.id,
            name: sport.name,
          })) || [],
          academies: result.data?.data?.academies?.map((academy) => ({
            id: academy.id,
            name: academy.name,
            sports: academy.sports,
          })) || [],
        });
      } catch (error) {
      }
    };

    fetchData();
  }, []);

  let filteredAcademies = data.academies.filter((academy) =>
    formData.sportId ? academy.sports.includes(Number(formData.sportId)) : true
  );

  if (formData.coachType?.value === "HEAD" || formData.coachType?.value === "JUNIOR") {
    filteredAcademies = filteredAcademies.filter(
      (academy) => Number(academy.id) !== Number(formData.coachTypeAcademyId)
    );
  }


  const selectedSport = data.sports.find(sport => sport.id === Number(formData.sportId));

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/coachList" className="breadcrumb-link">Coach List</Link> &gt; New Coach
      </div>
      <form className="create-coach-form" onSubmit={handleSubmit}>
        {/* Mobile Number */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="mobileNumber"
            className={`walkin-form ${formData.mobileNumber ? "has-value" : ""}`}
            value={formData.mobileNumber}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/\D/g, "");
              handleChange("mobileNumber", numericValue);
            }}
            placeholder="Enter Mobile Number"
            required
            maxLength={10}
          />
          {formData.mobileNumber && (
            <label htmlFor="mobileNumber">Mobile Number</label>
          )}
        </div>


        {/* Name */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="name"
            className={`walkin-form ${formData.name ? "has-value" : ""}`}
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Enter Name"
            required
          />
          {formData.name && (
            <label htmlFor="name">Name</label>
          )}
        </div>


        {/* About */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <textarea
            name="about"
            className={`walkin-form-comments ${formData.about ? "has-value" : ""}`}
            value={formData.about}
            onChange={(e) => handleChange("about", e.target.value)}
            placeholder="Write something about yourself or the facility"
            rows={4}
          />
          {formData.about && (
            <label htmlFor="about">About</label>
          )}
        </div>


        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            id="gender"
            name="gender"
            options={genderOptions}
            value={genderOptions.find((g) => g.value === formData.gender)}
            onChange={(selected) => handleChange("gender", selected.value)}
            placeholder="Select Gender"
            isClearable
            className={`walkin-form-dropdown ${formData.gender ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            styles={{
              control: (provided) => ({
                ...provided,
                padding: '5px',
              }),
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
          />
          {formData.gender && <label htmlFor="gender">Gender</label>}
        </div>




        {/* Sport ID */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            id="sport"
            name="sport"
            options={data.sports.map((sport) => ({
              value: sport.id,
              label: sport.name,
            }))}
            value={
              data.sports.find(sport => sport.id === Number(formData.sportId))
                ? { value: formData.sportId, label: selectedSport?.name }
                : null
            }
            onChange={(selected) => handleChange("sportId", selected.value)}
            placeholder="Select Sport"
            isClearable
            className={`walkin-form-dropdown ${formData.sportId ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            styles={{
              control: (provided) => ({
                ...provided,
                padding: '5px',
              }),
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
          />
          {formData.sportId && <label htmlFor="sport">Sport</label>}
        </div>


        {/* Coach Type */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            id="coachType"
            name="coachType"
            options={coachTypeOptions}
            value={formData.coachType}
            onChange={(selectedOption) => handleChange("coachType", selectedOption)}
            placeholder="Select Coach Type"
            isClearable
            className={`walkin-form-dropdown ${formData.coachType ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            styles={{
              control: (provided) => ({
                ...provided,
                padding: '5px',
              }),
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
          />
          {formData.coachType && <label htmlFor="coachType">Coach Type</label>}
        </div>



        {/* Independent Academy */}
        {formData.coachType?.value && (
          <div className="floating-select-form" style={{ width: '98%' }}>
            <Select
              id="coachTypeAcademyId"
              name="coachTypeAcademyId"
              options={data.academies
                .filter(academy => academy.sports.includes(Number(formData.sportId)))
                .map((academy) => ({
                  value: academy.id,
                  label: academy.name,
                }))}
              value={
                data.academies.find(academy => academy.id === Number(formData.coachTypeAcademyId))
                  ? {
                    value: formData.coachTypeAcademyId,
                    label: data.academies.find(academy => academy.id === Number(formData.coachTypeAcademyId))?.name,
                  }
                  : null
              }
              onChange={(selected) => handleChange("coachTypeAcademyId", selected.value)}
              placeholder="Select Academy"
              isClearable
              className={`walkin-form-dropdown ${formData.coachTypeAcademyId ? 'has-value' : ''}`}
              classNamePrefix="walkin-select"
              styles={{
                control: (provided) => ({
                  ...provided,
                  padding: '5px',
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: 'black',
                }),
              }}
            />
            {formData.coachTypeAcademyId && <label htmlFor="coachTypeAcademyId">Academy (CoachType)</label>}
          </div>
        )}


        {/* Role */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            id="role"
            name="role"
            options={roleOptions}
            value={roleOptions.find((option) => option.value === formData.role?.value)}
            onChange={(selected) => handleChange("role", selected)}
            placeholder="Select Role"
            isClearable
            className={`walkin-form-dropdown ${formData.role ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            styles={{
              control: (provided) => ({
                ...provided,
                padding: '5px',
              }),
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
          />
          {formData.role && <label htmlFor="role">Role</label>}
        </div>


        {/* Role-Based Academy Multi-Select */}
        {formData.role?.value === "CBU_AM" && (
          <div className="floating-select-form" style={{ width: '98%' }}>
            <Select
              id="academyIds"
              name="academyIds"
              options={filteredAcademies.map((academy) => ({
                value: academy.id,
                label: academy.name,
              }))}
              value={formData.academyIds
                .map((id) => {
                  const academy = data.academies.find((a) => a.id === Number(id));
                  return academy ? { value: academy.id, label: academy.name } : null;
                })
                .filter(Boolean)}
              onChange={(selected) =>
                handleChange("academyIds", selected ? selected.map((a) => a.value) : [])
              }
              isMulti
              placeholder="Select Academy"
              className={`walkin-form-dropdown ${formData.academyIds?.length ? 'has-value' : ''}`}
              classNamePrefix="walkin-select"
              isClearable={false}
              styles={{
                control: (provided) => ({
                  ...provided,
                  padding: '5px',
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: 'black',
                }),
              }}
            />
            {formData.academyIds?.length > 0 && (
              <label htmlFor="academyIds">Academy (Role-Based)</label>
            )}
          </div>
        )}

        {/* Experience Year & Month */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="experienceYear"
            className={`walkin-form ${formData.experienceYear ? 'has-value' : ''}`}
            value={formData.experienceYear}
            onChange={(e) => handleChange("experienceYear", e.target.value)}
            placeholder="Enter Experience (Years)"
            required
            min="0"
            onWheel={e => e.target.blur()}
            onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.experienceYear && (
            <label htmlFor="experienceYear">Experience (Years)</label>
          )}
        </div>


        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="experienceMonth"
            className={`walkin-form ${formData.experienceMonth ? 'has-value' : ''}`}
            value={formData.experienceMonth}
            onChange={(e) => handleChange("experienceMonth", parseInt(e.target.value, 10))}
            placeholder="Enter Experience (Months)"
            min="0"
            max="12"
            required
            onWheel={e => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.experienceMonth !== 0 && formData.experienceMonth && (
            <label htmlFor="experienceMonth">Experience (Months)</label>
          )}
        </div>


        {/* Joining Date */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="date"
            name="joiningDate"
            className={`walkin-form ${formData.joiningDate ? 'has-value' : ''}`}
            value={formData.joiningDate}
            onClick={(e) => e.target.showPicker()}
            onChange={(e) => handleChange("joiningDate", e.target.value)}
            placeholder="Select Joining Date"
          />
          {formData.joiningDate && (
            <label htmlFor="joiningDate">Joining Date</label>
          )}
        </div>

        {/* Submit Button */}
        <button type="submit" className="submit-button">
          {coachId ? "Update Coach" : "Create Coach"}
        </button>
      </form>
    </div>
  );
};

export default CreateCoach;